"use client";

import { MessageSquare, ArrowRight } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqItems = [{
  id: "item-1",
  number: "01",
  question: "How long does a build take?",
  answer: "Most custom website builds and AI optimization projects are completed within 2 to 4 weeks, depending on the scope of your existing digital assets and technical requirements."
}, {
  id: "item-2",
  number: "02",
  question: "Do you redesign or start fresh?",
  answer: "We assess your current platform first. If your existing site holds strong search authority, we perform a website cleanup and overlay specialized landing pages. If it is outdated, we build a fresh, AI-search ready architecture from the ground up."
}, {
  id: "item-3",
  number: "03",
  question: "Will I lose my current SEO rankings?",
  answer: "No. We implement strict 301 redirect mapping, retain technical metadata, and structure entity relations to preserve existing organic SEO while boosting discovery across generative AI engines."
}, {
  id: "item-4",
  number: "04",
  question: "What makes a site \"AI search ready\"?",
  answer: "An AI-ready site features direct schema data, LLM-friendly content structure, authoritative citation profiles, and clear entity resolution so models like ChatGPT, Claude, and Perplexity reliably cite your service."
}, {
  id: "item-5",
  number: "05",
  question: "Do you write the copy?",
  answer: "Yes. We write clear, high-converting, plain-English content optimized specifically for both human prospective clients and AI recommendation engines."
}, {
  id: "item-6",
  number: "06",
  question: "What happens after launch?",
  answer: "We provide monthly tracking, continuous LLM citation monitoring, and performance updates to maintain your top positioning as AI models update their indexes."
}];
export default function FaqAccordion() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section data-section-id="31006" id="faqaccordionsplit" ref={ref} className="bg-background py-24 md:py-36 text-foreground transition-opacity duration-700 ease-out">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <div className={`lg:col-span-4 transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="sticky top-28 space-y-6">
              <div className="inline-flex items-center gap-2 rounded-md bg-[var(--accent-deep-red)]/10 px-3 py-1 text-xs font-mono uppercase tracking-[0.25em] text-[var(--accent-deep-red)] border border-[var(--accent-deep-red)]/20">
                FAQ
              </div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold leading-tight">
                Got questions? We have <span className="italic text-[var(--accent-deep-red)] font-serif font-normal">answers</span>.
              </h2>
              
              <p className="text-muted-foreground text-base leading-relaxed">
                Clear answers about our AI search optimization, website rebuilds, review systems, and monthly visibility reporting.
              </p>

              <div className="pt-4 space-y-4 border-t border-border/40">
                <p className="text-sm text-muted-foreground font-medium">
                  Still have a question not listed here?
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button asChild variant="default" className="bg-[var(--accent-deep-red)] hover:bg-[var(--accent-deep-red)]/90 text-white rounded-md">
                    <Link to="/PricingPlansPage"><span>Get started </span><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-arrow-right size-4" aria-hidden="true"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg></Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-md border-border hover:border-[var(--accent-deep-red)]">
                    <Link className="underline cursor-pointer" to="/ContactPage">
                      <MessageSquare className="size-4 mr-2" />
                      <span>Free checkup</span>
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className={`lg:col-span-8 transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="group rounded-2xl border border-border bg-[hsl(240_10%_7%)] px-6 py-2 transition-all duration-300 hover:border-[var(--accent-deep-red)] hover:shadow-md">
                  <AccordionTrigger className="py-4 hover:no-underline text-left flex items-start justify-between gap-4 font-default font-semibold text-base sm:text-lg text-foreground group-hover:text-[var(--accent-deep-red)] transition-colors">
                    <span className="flex items-start gap-4">
                      <span className="font-mono text-sm text-muted-foreground group-hover:text-[var(--accent-deep-red)] transition-colors pt-0.5">
                        {item.number}
                      </span>
                      <span>{item.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pl-9 pr-4 pb-4">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

        </div>
      </div>
    </section>;
}